import React, { useRef, useState } from 'react';
import { MoreHorizontal, X } from 'lucide-react';
import ContextMenu from '../ui/ContextMenu';
import AvatarStack from '../ui/AvatarStack';
import { formatDoneDate } from '../../extensions/bundled/task-manager/taskCardMeta';
import { TaskKeyChip } from './TaskKeys';

/** A header button that opens the app's ContextMenu under itself, right-aligned. */
function MenuButton({ label, items, className, children, ariaLabel }) {
  const [at, setAt] = useState(null);
  const ref = useRef(null);
  const shown = (items || []).filter(Boolean);
  if (!shown.length) return null;
  return (
    <>
      <button
        ref={ref}
        type="button"
        className={className}
        aria-label={ariaLabel || label}
        aria-haspopup="menu"
        aria-expanded={Boolean(at)}
        onClick={() => {
          const r = ref.current?.getBoundingClientRect();
          setAt(r ? { x: r.right, y: r.bottom + 4 } : { x: 0, y: 0 });
        }}
      >
        {children}
      </button>
      {at && <ContextMenu x={at.x} y={at.y} align="end" items={shown} onClose={() => setAt(null)} className="task-modal-menu" />}
    </>
  );
}

const shortDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
};

/**
 * The task modal's head (owner, 06.10.2026): the title as a large editable
 * heading with when the task was created and last changed under it; on the
 * right one "…" menu with the actions — Copy link, Send to chat, Move to ›,
 * History, Delete — and the close button beside it. Esc and a click outside
 * the window close it too.
 */
export default function TaskModalHeader({ menuItems, viewers, taskKey = null, title, createdAt, updatedAt, closedTask = null, timeAgo, onTitleChange, onTitleFocus, onTitleCommit, onClose }) {
  const created = shortDate(createdAt);
  const updated = updatedAt && timeAgo ? timeAgo(updatedAt) : '';
  // "Done 10 Oct" when the task closed after done dates were recorded (doneAt).
  const closed = formatDoneDate(closedTask);
  return (
    <div className="task-modal-head">
      <div className="task-modal-toprow">
        <div className="task-modal-titlebox">
          <input
            type="text"
            className="task-detail-header-title-input task-modal-title"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            onFocus={onTitleFocus}
            onBlur={onTitleCommit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onTitleCommit();
                e.currentTarget.blur();
              }
            }}
            placeholder="Task title"
            aria-label="Task title"
          />
          {(taskKey || created || updated || closed) && (
            <div className="task-modal-dates">
              {/* The task's key (commit ↔ task links): what a commit message or branch names. */}
              <TaskKeyChip taskKey={taskKey} className="task-modal-key" />
              {created && <span>Created {created}</span>}
              {created && (updated || closed) && <span aria-hidden>·</span>}
              {closed && <span data-done-date="true">{closed.word === 'dismissed' ? 'Dismissed' : 'Done'} {closed.text.slice(closed.word.length + 1)}</span>}
              {closed && updated && <span aria-hidden>·</span>}
              {updated && <span>Updated {updated}</span>}
            </div>
          )}
        </div>
        <div className="task-modal-actions">
          {viewers?.length > 0 && <AvatarStack members={viewers} />}
          <MenuButton label="Task actions" items={menuItems} className="task-modal-icon-btn task-modal-more">
            <MoreHorizontal size={18} />
          </MenuButton>
          <button type="button" className="task-modal-icon-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
